import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import LoadingSpinner from '../components/LoadingSpinner'
import PoolStandingsPage from './PoolStandingsPage'
import HorseRacingPoolStandingsPage from './HorseRacingPoolStandingsPage'

const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'https://api.midwestrailbirds.com/v1'

interface Pool {
  poolId: string
  name: string
  type: 'football' | 'horse-racing' | 'cornhole'
}

export default function PoolStandingsRouter() {
  const { poolId } = useParams<{ poolId: string }>()
  const { getAccessTokenSilently } = useAuth0()
  const [pool, setPool] = useState<Pool | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (poolId) {
      fetchPool()
    }
  }, [poolId])

  const fetchPool = async () => {
    try {
      setLoading(true)
      const token = await getAccessTokenSilently()
      
      const response = await fetch(`${API_BASE_URL}/pools/${poolId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch pool: ${response.statusText}`)
      }

      const data = await response.json()
      setPool(data.pool)
    } catch (err) {
      console.error('Error fetching pool:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch pool')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Error</h1>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-8">
            <div className="text-4xl mb-4">❌</div>
            <p className="text-gray-600 dark:text-gray-300">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  if (!pool) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Pool Not Found</h1>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-8">
            <div className="text-4xl mb-4">❓</div>
            <p className="text-gray-600 dark:text-gray-300">The requested pool could not be found.</p>
          </div>
        </div>
      </div>
    )
  }

  // Route to appropriate standings page based on pool type
  switch (pool.type) {
    case 'football':
      return <PoolStandingsPage />
    case 'horse-racing':
      return <HorseRacingPoolStandingsPage />
    case 'cornhole':
      // Future: Add cornhole standings page
      return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Cornhole Standings</h1>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-8">
              <div className="text-4xl mb-4">🌽</div>
              <p className="text-gray-600 dark:text-gray-300">Cornhole standings coming soon!</p>
            </div>
          </div>
        </div>
      )
    default:
      return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Unknown Pool Type</h1>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-8">
              <div className="text-4xl mb-4">❓</div>
              <p className="text-gray-600 dark:text-gray-300">
                This pool type ({pool.type}) is not yet supported.
              </p>
            </div>
          </div>
        </div>
      )
  }
}
